/**
 * ShortcutSettings Component
 * Full shortcut configuration UI for Settings page
 * Features:
 * - 4 cascading dropdowns: Page → Table → Action → Key Type
 * - Visual keyboard for key selection
 * - Conflict detection (same key on same page disabled)
 * - Admin: apply to all users or selected users
 * - CRUD table of all configured shortcuts
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import api from '../../services/api';
import KeyboardLayoutPicker from './KeyboardLayoutPicker';
import { SHORTCUT_PAGES, keyToDisplay } from '../../config/shortcutConfig';

const ADMIN_USER_IDS = ['1147596', '1935095'];

const ShortcutSettings = () => {
  const { userId } = useSelector((state) => state.auth);
  const isAdmin = ADMIN_USER_IDS.includes(String(userId));

  // Form state
  const [selectedPage, setSelectedPage] = useState('');
  const [selectedTable, setSelectedTable] = useState('');
  const [selectedAction, setSelectedAction] = useState('');
  const [keyType, setKeyType] = useState('single');
  const [modifierKey, setModifierKey] = useState(null);
  const [shortcutKey, setShortcutKey] = useState(null);
  const [applyToAll, setApplyToAll] = useState(false);
  const [applyToUsers, setApplyToUsers] = useState([]);

  // Data state
  const [shortcuts, setShortcuts] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editingId, setEditingId] = useState(null);

  // Derived data
  const pageConfig = SHORTCUT_PAGES.find(p => p.id === selectedPage);
  const tables = pageConfig?.tables || [];
  const tableConfig = tables.find(t => t.id === selectedTable);
  const actions = tableConfig?.actions || [];
  const actionConfig = actions.find(a => a.id === selectedAction);

  // Keys already used on this page by this user (for conflict detection)
  const usedKeysOnPage = shortcuts
    .filter(s => s.page_id === selectedPage && s.user_id === userId)
    .map(s => {
      if (s.key_type === 'single') return s.shortcut_key;
      return null; // Multi-key combos don't block single keys
    })
    .filter(Boolean);

  // For multi-key: keys already used as multi combos with the selected modifier
  const usedMultiKeys = shortcuts
    .filter(s => s.page_id === selectedPage && s.user_id === userId && s.key_type === 'multi' && s.modifier_key === modifierKey)
    .map(s => s.shortcut_key);

  // Fetch shortcuts
  const fetchShortcuts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/keyboard-shortcuts');
      if (response.data.success) {
        setShortcuts(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching shortcuts:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch users (admin only)
  const fetchUsers = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const response = await api.get('/keyboard-shortcuts/users');
      if (response.data.success) {
        setAllUsers(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchShortcuts();
    fetchUsers();
  }, [fetchShortcuts, fetchUsers]);

  // Reset downstream selections when parent changes
  useEffect(() => { setSelectedTable(''); setSelectedAction(''); }, [selectedPage]);
  useEffect(() => { setSelectedAction(''); }, [selectedTable]);
  useEffect(() => { setModifierKey(null); setShortcutKey(null); }, [keyType]);

  // Handle save
  const handleSave = async () => {
    if (!selectedPage || !selectedTable || !selectedAction || !shortcutKey) {
      setError('Please complete all selections');
      return;
    }
    if (keyType === 'multi' && !modifierKey) {
      setError('Please select a modifier key for multi-key shortcut');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const payload = {
        page_id: selectedPage,
        table_id: selectedTable,
        action_id: selectedAction,
        action_label: actionConfig?.label || selectedAction,
        key_type: keyType,
        modifier_key: keyType === 'multi' ? modifierKey : null,
        shortcut_key: shortcutKey,
        is_admin_shortcut: isAdmin && (applyToAll || applyToUsers.length > 0),
        apply_to_all: isAdmin ? applyToAll : false,
        apply_to_users: isAdmin && applyToUsers.length > 0 ? applyToUsers : null,
      };

      if (editingId) {
        await api.put(`/keyboard-shortcuts/${editingId}`, payload);
        setSuccess('Shortcut updated successfully');
      } else {
        await api.post('/keyboard-shortcuts', payload);
        setSuccess('Shortcut created successfully');
      }

      // Reset only key/admin fields; keep page/table/action selections
      setKeyType('single');
      setModifierKey(null);
      setShortcutKey(null);
      setApplyToAll(false);
      setApplyToUsers([]);
      setEditingId(null);
      fetchShortcuts();
    } catch (err) {
      const errMsg = err.response?.data?.error || err.message;
      setError(errMsg);
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setSelectedPage('');
    setSelectedTable('');
    setSelectedAction('');
    setKeyType('single');
    setModifierKey(null);
    setShortcutKey(null);
    setApplyToAll(false);
    setApplyToUsers([]);
    setEditingId(null);
  };

  // Handle edit
  const handleEdit = (shortcut) => {
    setEditingId(shortcut.id);
    setSelectedPage(shortcut.page_id);
    setTimeout(() => {
      setSelectedTable(shortcut.table_id);
      setTimeout(() => {
        setSelectedAction(shortcut.action_id);
        setKeyType(shortcut.key_type);
        if (shortcut.key_type === 'multi') {
          setModifierKey(shortcut.modifier_key);
        }
        setShortcutKey(shortcut.shortcut_key);
      }, 50);
    }, 50);
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this shortcut?')) return;
    try {
      await api.delete(`/keyboard-shortcuts/${id}`);
      setSuccess('Shortcut deleted');
      fetchShortcuts();
    } catch (err) {
      setError(err.response?.data?.error || 'Delete failed');
    }
  };

  // Get page label by id
  const getPageLabel = (pageId) => SHORTCUT_PAGES.find(p => p.id === pageId)?.label || pageId;
  const getTableLabel = (pageId, tableId) => {
    const page = SHORTCUT_PAGES.find(p => p.id === pageId);
    return page?.tables?.find(t => t.id === tableId)?.label || tableId;
  };

  return (
    <div className="space-y-4">
      {/* Shortcut Builder */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-4">
        <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
          ⌨️ {editingId ? 'Edit Shortcut' : 'Create New Shortcut'}
          {editingId && (
            <button onClick={resetForm} className="text-xs text-gray-500 hover:text-gray-700 ml-auto">
              ✕ Cancel Edit
            </button>
          )}
        </h3>

        {/* 4 Cascading Dropdowns */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          {/* 1. Page Selection */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">1. Select Page</label>
            <select
              value={selectedPage}
              onChange={(e) => setSelectedPage(e.target.value)}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value="">-- Choose Page --</option>
              {SHORTCUT_PAGES.map(page => (
                <option key={page.id} value={page.id}>{page.label}</option>
              ))}
            </select>
          </div>

          {/* 2. Table Selection */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">2. Select Table/Section</label>
            <select
              value={selectedTable}
              onChange={(e) => setSelectedTable(e.target.value)}
              disabled={!selectedPage}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white disabled:opacity-50"
            >
              <option value="">-- Choose Table --</option>
              {tables.map(table => (
                <option key={table.id} value={table.id}>{table.label}</option>
              ))}
            </select>
          </div>

          {/* 3. Action Selection */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">3. Select Action</label>
            <select
              value={selectedAction}
              onChange={(e) => setSelectedAction(e.target.value)}
              disabled={!selectedTable}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white disabled:opacity-50"
            >
              <option value="">-- Choose Action --</option>
              {actions.map(action => (
                <option key={action.id} value={action.id}>{action.label}</option>
              ))}
            </select>
          </div>

          {/* 4. Key Type */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">4. Key Type</label>
            <div className="flex gap-1">
              <button
                onClick={() => setKeyType('single')}
                disabled={!selectedAction}
                className={`flex-1 px-2 py-1.5 text-xs font-semibold rounded-lg border transition-all ${
                  keyType === 'single'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                } disabled:opacity-50`}
              >
                Single Key
              </button>
              <button
                onClick={() => setKeyType('multi')}
                disabled={!selectedAction}
                className={`flex-1 px-2 py-1.5 text-xs font-semibold rounded-lg border transition-all ${
                  keyType === 'multi'
                    ? 'bg-purple-600 text-white border-purple-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-purple-400'
                } disabled:opacity-50`}
              >
                Multi Key
              </button>
            </div>
          </div>
        </div>

        {/* Selected Summary */}
        {selectedAction && (
          <div className="mb-3 p-2 bg-blue-50 rounded-lg border border-blue-200">
            <div className="text-xs text-blue-800">
              <span className="font-semibold">Configuring:</span>{' '}
              <span className="font-mono">{getPageLabel(selectedPage)}</span> → {' '}
              <span className="font-mono">{tableConfig?.label}</span> → {' '}
              <span className="font-bold">{actionConfig?.label}</span>
            </div>
          </div>
        )}

        {/* Keyboard Layouts */}
        {selectedAction && (
          <div className="space-y-3">
            {keyType === 'multi' && (
              <div className="border border-purple-200 rounded-lg p-3 bg-purple-50/50">
                <KeyboardLayoutPicker
                  mode="modifier"
                  selectedKey={modifierKey}
                  onKeySelect={(key) => setModifierKey(key)}
                  title="Step 1: Select Modifier Key (Ctrl / Shift / Alt / F-key)"
                />
              </div>
            )}

            {(keyType === 'single' || (keyType === 'multi' && modifierKey)) && (
              <div className="border border-blue-200 rounded-lg p-3 bg-blue-50/50">
                <KeyboardLayoutPicker
                  mode="key"
                  selectedKey={shortcutKey}
                  disabledKeys={keyType === 'single' ? usedKeysOnPage : usedMultiKeys}
                  onKeySelect={(key) => setShortcutKey(key)}
                  title={keyType === 'multi' ? `Step 2: Select Key (${modifierKey} + ?)` : 'Select Keyboard Key'}
                />
              </div>
            )}
          </div>
        )}

        {/* Admin Options */}
        {isAdmin && selectedAction && shortcutKey && (
          <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
            <div className="text-xs font-bold text-amber-800 mb-2">👑 Admin Options</div>
            <div className="flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={applyToAll}
                  onChange={(e) => { setApplyToAll(e.target.checked); if (e.target.checked) setApplyToUsers([]); }}
                  className="w-3.5 h-3.5"
                />
                Apply to all users
              </label>
              {!applyToAll && (
                <div className="flex items-center gap-1">
                  <span className="text-xs text-gray-600">Or select users:</span>
                  <select
                    multiple
                    value={applyToUsers}
                    onChange={(e) => setApplyToUsers(Array.from(e.target.selectedOptions, o => o.value))}
                    className="px-2 py-1 text-xs border border-gray-300 rounded max-h-20"
                    size={3}
                  >
                    {allUsers.filter(u => !ADMIN_USER_IDS.includes(String(u.user_id))).map(u => (
                      <option key={u.user_id} value={u.user_id}>{u.user_name || u.user_id}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Preview & Save */}
        {shortcutKey && (
          <div className="mt-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-600">Shortcut:</span>
              <div className="flex items-center gap-1">
                {keyType === 'multi' && modifierKey && (
                  <>
                    <kbd className="px-2 py-1 bg-purple-100 text-purple-800 border border-purple-300 rounded text-xs font-mono font-bold">
                      {keyToDisplay(modifierKey)}
                    </kbd>
                    <span className="text-gray-400 font-bold">+</span>
                  </>
                )}
                <kbd className="px-2 py-1 bg-blue-100 text-blue-800 border border-blue-300 rounded text-xs font-mono font-bold">
                  {keyToDisplay(shortcutKey)}
                </kbd>
              </div>
              <span className="text-xs text-gray-500">→</span>
              <span className="text-xs font-semibold text-gray-700">{actionConfig?.label}</span>
            </div>
            <div className="flex items-center gap-2">
              {editingId && (
                <button
                  onClick={resetForm}
                  className="px-3 py-1.5 text-xs bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
              )}
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-1.5 text-xs font-bold bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 shadow-md"
              >
                {saving ? '⏳ Saving...' : editingId ? '💾 Update Shortcut' : '💾 Save Shortcut'}
              </button>
            </div>
          </div>
        )}

        {/* Messages */}
        {error && (
          <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">
            ❌ {error}
          </div>
        )}
        {success && (
          <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg text-xs text-green-700">
            ✅ {success}
          </div>
        )}
      </div>

      {/* Configured Shortcuts Table */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-gray-800">📋 Configured Shortcuts</h3>
          <span className="text-xs text-gray-500">{shortcuts.length} shortcut{shortcuts.length !== 1 ? 's' : ''}</span>
        </div>

        {loading ? (
          <div className="text-center py-8 text-gray-500 text-xs">Loading...</div>
        ) : shortcuts.length === 0 ? (
          <div className="text-center py-8 text-gray-500 text-xs">
            No shortcuts configured yet. Create one above.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-2 py-1.5 text-left font-semibold text-gray-600">Page</th>
                  <th className="px-2 py-1.5 text-left font-semibold text-gray-600">Table</th>
                  <th className="px-2 py-1.5 text-left font-semibold text-gray-600">Action</th>
                  <th className="px-2 py-1.5 text-center font-semibold text-gray-600">Shortcut</th>
                  {isAdmin && <th className="px-2 py-1.5 text-center font-semibold text-gray-600">Scope</th>}
                  <th className="px-2 py-1.5 text-center font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {shortcuts.map((s) => (
                  <tr key={s.id} className="border-b border-gray-100 hover:bg-blue-50/50 transition-colors">
                    <td className="px-2 py-1.5 text-gray-700">{getPageLabel(s.page_id)}</td>
                    <td className="px-2 py-1.5 text-gray-600">{getTableLabel(s.page_id, s.table_id)}</td>
                    <td className="px-2 py-1.5 font-medium text-gray-800">{s.action_label}</td>
                    <td className="px-2 py-1.5 text-center">
                      <div className="inline-flex items-center gap-1">
                        {s.key_type === 'multi' && s.modifier_key && (
                          <>
                            <kbd className="px-1.5 py-0.5 bg-purple-100 text-purple-800 border border-purple-200 rounded text-[10px] font-mono font-bold">
                              {keyToDisplay(s.modifier_key)}
                            </kbd>
                            <span className="text-gray-400 text-[10px]">+</span>
                          </>
                        )}
                        <kbd className="px-1.5 py-0.5 bg-blue-100 text-blue-800 border border-blue-200 rounded text-[10px] font-mono font-bold">
                          {keyToDisplay(s.shortcut_key)}
                        </kbd>
                      </div>
                    </td>
                    {isAdmin && (
                      <td className="px-2 py-1.5 text-center">
                        {s.is_admin_shortcut ? (
                          <span className="px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded text-[10px] font-medium">
                            {s.apply_to_all ? '👑 All Users' : '👑 Custom'}
                          </span>
                        ) : (
                          <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-[10px]">
                            Personal
                          </span>
                        )}
                      </td>
                    )}
                    <td className="px-2 py-1.5 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => handleEdit(s)}
                          className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 text-[10px] font-medium"
                        >
                          ✏️ Edit
                        </button>
                        <button
                          onClick={() => handleDelete(s.id)}
                          className="px-2 py-0.5 bg-red-100 text-red-700 rounded hover:bg-red-200 text-[10px] font-medium"
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShortcutSettings;
